
import { BaseRepository } from '@/shared/infrastructure/BaseRepository';

interface ConversationData {
  id: string;
  userId: string;
  title: string;
  personality?: string;
  durationSeconds?: number;
  totalMessages: number;
  emotionSummary?: Record<string, any>;
  topics?: any[];
  contextKeywords?: string[];
  createdAt: Date;
  updatedAt: Date;
  lastAccessedAt?: Date;
}

interface CreateConversationData {
  userId: string;
  title: string;
  personality?: string;
}

export class ConversationRepository extends BaseRepository {

  async findById(id: string): Promise<ConversationData | null> {
    const { data, error } = await this.getClient()
      .from('voice_conversations')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error(`Erro ao buscar conversa por ID: ${error.message}`);
      return null;
    }

    return data ? this.mapToConversationData(data) : null;
  }

  async findByUserId(userId: string): Promise<ConversationData[]> {
    const { data, error } = await this.getClient()
      .from('voice_conversations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar conversas por usuário: ${error.message}`);
    }

    return (data || []).map(item => this.mapToConversationData(item));
  }

  async create(data: CreateConversationData): Promise<ConversationData> {
    const { data: createdData, error } = await this.getClient()
      .from('voice_conversations')
      .insert({
        user_id: data.userId,
        title: data.title,
        personality: data.personality || 'empathetic'
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Erro ao criar conversa: ${error.message}`);
    }

    return this.mapToConversationData(createdData);
  }

  private mapToConversationData(data: any): ConversationData {
    return {
      id: data.id,
      userId: data.user_id,
      title: data.title,
      personality: data.personality,
      durationSeconds: data.duration_seconds,
      totalMessages: data.total_messages,
      emotionSummary: data.emotion_summary,
      topics: data.topics,
      contextKeywords: data.context_keywords,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      lastAccessedAt: data.last_accessed_at ? new Date(data.last_accessed_at) : undefined
    };
  }
}
